import { api } from '../../../shared/api/client';
import {
  type ListEnvelope,
  normalizeListEnvelope,
  toGenexusParams,
} from '../../../shared/api/genexus-query';
import type { CrudApi } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import {
  makeDocumentoFiscalId,
  type CompanyDocumentoFiscalDto,
  type CompanyDocumentoFiscalInput,
  type CompanyDocumentoFiscalListItem,
} from './types';

/**
 * Fábrica do CRUD aninhado em uma Empresa. `companyId` fica fechado no escopo,
 * espelhando o pattern de `createCompanyAddressesApi`.
 *
 * TId = string (`"<modelo>/<serie>"`). Como o helper só concatena `${BASE}/${id}`,
 * a barra interna casa naturalmente com o path REST `/{modelo}/{serie}`.
 */
export function createCompanyDocumentosFiscaisApi(
  companyId: number,
): CrudApi<
  CompanyDocumentoFiscalListItem,
  CompanyDocumentoFiscalDto,
  CompanyDocumentoFiscalInput,
  string
> {
  const BASE = `/v1/companies/${companyId}/documentos-fiscais`;

  return {
    async list(query: DataTableQuery): Promise<ListEnvelope<CompanyDocumentoFiscalListItem>> {
      const { data } = await api.get<{
        data: Omit<CompanyDocumentoFiscalListItem, 'id'>[];
        meta: { total: number; pagina?: number; porPagina?: number };
      }>(BASE, { params: toGenexusParams(query) });
      // Upstream não emite `id` (PK composta); sintetizamos `"<modelo>/<serie>"`
      // para satisfazer o `DataTable` (exige `id` na row) e fechar o contrato
      // de TId=string usado pelos hooks CRUD.
      const normalized = normalizeListEnvelope(data);
      return {
        ...normalized,
        data: normalized.data.map((row) => ({
          ...row,
          id: makeDocumentoFiscalId(row.modelo, row.serie),
        })),
      };
    },

    async get(id: string): Promise<CompanyDocumentoFiscalDto> {
      const { data } = await api.get<{ data: Omit<CompanyDocumentoFiscalDto, 'id'> }>(
        `${BASE}/${id}`,
      );
      return { ...data.data, id: makeDocumentoFiscalId(data.data.modelo, data.data.serie) };
    },

    async create(input: CompanyDocumentoFiscalInput): Promise<CompanyDocumentoFiscalDto> {
      const { data } = await api.post<{ data: Omit<CompanyDocumentoFiscalDto, 'id'> }>(BASE, input);
      return { ...data.data, id: makeDocumentoFiscalId(data.data.modelo, data.data.serie) };
    },

    async update(
      id: string,
      input: CompanyDocumentoFiscalInput,
    ): Promise<CompanyDocumentoFiscalDto> {
      const { data } = await api.put<{ data: Omit<CompanyDocumentoFiscalDto, 'id'> }>(
        `${BASE}/${id}`,
        input,
      );
      return { ...data.data, id: makeDocumentoFiscalId(data.data.modelo, data.data.serie) };
    },

    async remove(id: string): Promise<void> {
      await api.delete(`${BASE}/${id}`);
    },
  };
}
